import { defineQuery, groq } from 'next-sanity';
import {
  heroBannerSlideshowProjection,
  PAGE_BUILDER_PROJECTION,
} from '../queries/pageBuilderProjection';
import { bynderImageProjection } from '../queries/projection';
import { namespaceDocumentId, namespaceType } from '../queries/namespace';
import { SEO_PROJECTION } from '../queries/seo';

const structaProductLevel1Type = namespaceType('structaProductLevel1');
const structaProductLevel2Type = namespaceType('structaProductLevel2');
const structaProductLevel3Type = namespaceType('structaProductLevel3');
const articleType = namespaceType('article');
const settingsType = namespaceType('settings');
const pageType = namespaceType('page');
const productsListType = namespaceType('productsList');
const productType = namespaceType('product');
const documentPageType = namespaceType('documentPage');
const documentFileType = namespaceType('documentFile');
const storeLocatorType = namespaceType('storeLocator');
const contactUsType = namespaceType('contactUs');
const faqsType = namespaceType('faqs');
const homePageType = namespaceType('homePage');
const homePageId = namespaceDocumentId('homePage');
const genericPageType = namespaceType('genericPage');
const redirectType = namespaceType('redirect');
const linkType = namespaceType('link');

export const GET_LEVEL_1_PRODUCTS = defineQuery(`
  *[_type == "${structaProductLevel1Type}"]{
    _id,
    _type,
    name,
    "slug": slug.current}
  `);

export const GET_SUB_LEVEL_PRODUCTS = defineQuery(`
  *[
    (_type == "${structaProductLevel2Type}" && references($level1Id)) ||
    (_type == "${structaProductLevel3Type}" && references($level1Id))
  ]{
    _id,
    _type,
    name,
    "slug": slug.current,
    "parentPage": parentPage->{
      _id,
      _type,
      name,
      "slug": slug.current
    }
  }
`);
export const structaLevel3Slugs = defineQuery(`
  *[(_type == "${structaProductLevel2Type}" && defined(slug.current)) || (_type == "${structaProductLevel3Type}" && defined(slug.current))]
  {"slug": slug.current}
`);

export const articleSlugs = defineQuery(`
  *[_type == "${articleType}" && defined(slug.current)]
  {"slug": slug.current}
`);

export const settingsQuery = defineQuery(`*[_type == "${settingsType}"][0]`);

const postFields = /* groq */ `
  _id,
  "status": select(_originalId in path("drafts.**") => "draft", "published"),
  "title": coalesce(title, "Untitled"),
  "slug": slug.current,
  excerpt,
  coverImage,
  "date": coalesce(date, _updatedAt),
  "author": author->{firstName, lastName, picture},
`;

export const LINK_REFERENCE = /* groq */ `
  _type == "${linkType}" => {
    "page": page->slug.current,
    "post": post->slug.current
  }
`;

// const linkFields = /* groq */ `
//   link {
//     ...,
//     ${linkReference}
//   }
// `;

export const getPageQuery = defineQuery(/* groq */ `
  *[_type == '${pageType}' && slug.current == $slug][0]{
    _id,
    _type,
    name,
    slug,
    heading,
    subheading,
    ${PAGE_BUILDER_PROJECTION},
    ${SEO_PROJECTION},
  }
`);

export const structaLevel2Slugs = defineQuery(/* groq */ `
  *[_type == "${structaProductLevel2Type}" && defined(slug.current)]{
    "slug": slug.current
  }
`);

export const getStructaLevel2ProductQuery = defineQuery(/* groq */ `
  *[_type == "${structaProductLevel2Type}" && slug.current == $slug][0]{
    _id,
    _type,
    name,
    slug,
    description,
    isIncludedContactSupport,
    ${PAGE_BUILDER_PROJECTION},
    ${SEO_PROJECTION},
  }
`);

export const getStructaLevel1ProductQuery = defineQuery(/* groq */ `
  *[_type == "${structaProductLevel1Type}" && slug.current == $slug][0]{
    _id,
    _type,
    name,
    heading,
    subheading,
    isIncludedContactSupport,
    ${PAGE_BUILDER_PROJECTION},
    ${SEO_PROJECTION},
  }
`);

export const getStructaLevel3ProductQuery = defineQuery(/* groq */ `
  *[_type == '${structaProductLevel3Type}' && slug.current == $slug][0]{
    _id,
    _type,
    name,
    slug,
    heading,
    subheading,
    productSummary,
    isIncludedContactSupport,
    "thumbnailImage": {
      "image": customSeo.metaImage,
      "bynderImage": customSeo.metaImage,
      "imageDescription": customSeo.metaImage.textMetaproperties[name == 'Alt_text'][0].value
    },
    ${PAGE_BUILDER_PROJECTION},
    ${SEO_PROJECTION},
  }
`);

export const getArticleQuery = defineQuery(/* groq */ `
  *[_type == '${articleType}' && slug.current == $slug][0]{
    ...,
    _id,
    _type,
    title,
    slug,
    author->{
      _id, name,
      ${bynderImageProjection('avatar', 'bynderAvatar')}
    },
    "articleCategory": articleCategory->{_id, name},
    "articleProducts": coalesce(articleProducts[]->{_id, name}, []),
    "articleApplications": coalesce(articleApplications[]->{_id, name}, []),
    "shareIcons": shareIcons[]->{
      shareType,
      "sharePath": select(
        shareType == "facebook" => "https://www.facebook.com/sharer/sharer.php?u=" + ^.type + "/" + ^.slug.current,
        shareType == "linkedin" => "https://www.linkedin.com/shareArticle?mini=true&url=" + ^.type + "/" + ^.slug.current,
        shareType == "pinterest" => "https://pinterest.com/pin/create/button/?url=" + ^.type + "/" + ^.slug.current,
        shareType == "linked" => "/" + ^.type + "/" + ^.slug.current,
        shareType == "share" => "mailto:?subject=" + ^.title + "&body=" + ^.title + "%0A/" + ^.type + "/" + ^.slug.current,
      )
    },
    heroBanner{
      ${heroBannerSlideshowProjection}
    },
    isIncludedContactSupport,
    isIncludedAskGreg,
    ${PAGE_BUILDER_PROJECTION},
    ${SEO_PROJECTION},
  }
`);

export const sitemapData = defineQuery(`
  [
    ...*[_type == "${homePageType}" && _id == "${homePageId}"]{
      _type,
      _updatedAt
    },
    ...*[_type == "${genericPageType}" && defined(slug.current)]{
      _type,
      "slug": slug.current,
      _updatedAt
    },
    ...*[_type == "${articleType}" && defined(slug.current)]{
      _type,
      "slug": slug.current,
      "category": type,
      _updatedAt
    },
    ...*[_type == "${productsListType}" && defined(slug.current) && defined(category->slug.current)]{
      _type,
      "slug": slug.current,
      "parentSlug": category->slug.current,
      _updatedAt
    },
    ...*[_type == "${structaProductLevel1Type}" && defined(slug.current)]{
      _type,
      "slug": slug.current,
      _updatedAt
    },
    ...*[_type == "${productType}" && defined(slug.current) && defined(subcategory->slug.current) && defined(subcategory->parentCategory->slug.current)]{
      _type,
      "slug": slug.current,
      "subcategorySlug": subcategory->slug.current,
      "parentSlug": subcategory->parentCategory->slug.current,
      _updatedAt
    },
    ...*[_type == "${structaProductLevel2Type}" && Orchard_Type == "${structaProductLevel2Type}" && defined(slug.current) && defined(parentPage->slug.current)]{
      _type,
      "slug": slug.current,
      "parentSlug": parentPage->slug.current,
      _updatedAt
    },
    ...*[_type == "${structaProductLevel3Type}" && defined(slug.current) && defined(parentPage->slug.current)]{
      _type,
      "slug": slug.current,
      "parentSlug": parentPage->slug.current,
      "grandparentSlug": parentPage->parentPage->slug.current,
      _updatedAt
    },
    ...*[_type == "${documentPageType}" && defined(slug.current)]{
      _type,
      "slug": slug.current,
      _updatedAt
    },
    ...*[_type == "${documentFileType}" && defined(bynderDocument)]{
      _type,
      "fileUrl": coalesce(
        bynderDocument.originalUrl,
        bynderDocument.selectedUrl,
        bynderDocument.files.original.url
      ),
      bynderDocument,
      _updatedAt
    },
    ...*[_type == "${storeLocatorType}"]{
      _type,
      _updatedAt
    },
    ...*[_type == "${contactUsType}"]{
      _type,
      _updatedAt
    },
    ...*[_type == "${faqsType}"]{
      _type,
      _updatedAt
    }
  ]
`);

export const allPostsQuery = defineQuery(`
  *[_type == "post" && defined(slug.current)] | order(date desc, _updatedAt desc) {
    ${postFields}
  }
`);

export const morePostsQuery = defineQuery(`
  *[_type == "post" && _id != $skip && defined(slug.current)] | order(date desc, _updatedAt desc) [0...$limit] {
    ${postFields}
  }
`);

export const postQuery = defineQuery(`
  *[_type == "post" && slug.current == $slug] [0] {
    content[]{
    ...,
    markDefs[]{
      ...,
      ${LINK_REFERENCE}
    }
  },
    ${postFields}
  }
`);

export const postPagesSlugs = defineQuery(`
  *[_type == "post" && defined(slug.current)]
  {"slug": slug.current}
`);

export const pagesSlugs = defineQuery(`
  *[_type == "${pageType}" && defined(slug.current)]
  {"slug": slug.current}
`);

export const getStoreLocatorList = defineQuery(`
  *[_type == "${storeLocatorType}"][0]{
    _id,
    _type,
    _createdAt,
    _updatedAt,
    _rev,
    name,
    headline,
    isIncludedProductRangeFilter,
    notFoundHeading,
    notFoundText,
    getDirectionLabel,
    listViewLabel,
    mapViewLabel,
    // Fetch the full store data for each reference in the storesList array.
    "storesList": storesList[]->{
      _id,
      _type,
      // Add other fields you want to fetch from the SingleStore document
      name,
      address,
      location,
      // etc.
    },
    isIncludedContactSupport
  }
`);

export const PRODUCT_BY_SLUG_QUERY = `
*[_type == "${productType}" && slug.current == $slug][0] {
  _id,
  name,
  slug,
  description,
  thumbnail,
  "category": category->{
    _id,
    name
  },
  "subcategory": subcategory->{
    _id,
    name
  },
  "image": thumbnail,
  "viewDetailLink": {
    "type": "internal",
    "url": "/products/" + slug.current
  },
  ${SEO_PROJECTION}
}
`;

export const blogDetailSlugs = groq`
  *[_type == "blogDetail" && defined(slug.current)][]{
    "slug": slug.current
  }
`;

export const blogDetailQuery = groq`
  *[_type == "blogDetail" && slug.current == $slug][0]{
    _id,
    title,
  }
`;

export const redirectQuery = groq`*[_type == "${redirectType}"]{
  "source":source.current,
  "destination":destination.current,
  externalUrl,
  permanent
}`;
